import { isResource, resolveId } from '@medplum/core'
import type {
  CodeableConcept,
  Coding,
  Encounter,
  Medication,
  MedicationRequest,
  Organization,
  Patient,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes'
import { differenceInDays, format, parseISO } from 'date-fns'
import { set as _set, compact, orderBy } from 'lodash-es'
import {
  codeableConcept,
  codeFromCodeableConcept,
  displayFromCodeableConcept,
} from '#src/codeableConcept.ts'
import {
  ELATION_SANDBOX_ID_SYSTEM,
  GRANERX_FILLER_ORDER_SYSTEM,
  identifierTypeFiller,
  MEDICATION_REQUEST_DISCONTINUED_BY_SYSTEM,
  MEDICATION_REQUEST_GRANERX_ORDER_SYSTEM,
  MEDICATION_REQUEST_IS_PERMANENT_SYSTEM,
  MEDICATION_REQUEST_ORDER_TYPE_SYSTEM,
  MEDICATION_REQUEST_PHARMACY_RECEPTION_SYSTEM,
  MEDICATION_REQUEST_PREVIOUS_DISPENSER_SYSTEM,
  MEDICATION_REQUEST_PHARMACY_TRANSMISSION_ISSUE_RESOLVED_AT_SYSTEM,
  MEDICATION_REQUEST_PHARMACY_TRANSMISSION_ISSUE_SYSTEM,
  MEDICATION_REQUEST_PHARMACY_TRANSMISSION_SYSTEM,
  MEDICATION_REQUEST_STATUS_REASON_SYSTEM,
  MEDICATION_REQUEST_STOPPED_ENCOUNTER_SYSTEM,
  MEDICATION_REQUEST_THREAD_ID_SYSTEM,
  MEDICATION_REQUEST_TYPE_SYSTEM,
  MedicationRequestType,
  RX_NORM_SYSTEM,
} from '#src/codeSystems.ts'
import { formatDateTime, today } from '#src/dates.ts'
import { Extension, Tag } from '#src/decorators.ts'
import type { JsonObject, JsonValue } from '#src/json.ts'
import {
  isPrnText,
  parseDailyDoseFromText,
} from '#src/models/medicationDosage.ts'
import { formatSigSummary } from '#src/models/medicationSig.ts'
import { Signable } from '#src/models/signable.ts'
import { createReference } from '#src/references.ts'
import type { QueryTypes } from '#src/search.ts'
import { identifier, isOfType, parseReferenceString } from '#src/utils.ts'

/*
 * Pharmacy transmission tracking - captures outgoing prescription state from Elation (placer side)
 */
export interface PharmacyTransmission {
  state: string
  type: string
  detail: string
  occurredAt: string
  /** @deprecated Use pharmacyReception for GraneRx data. Kept for backward compat with existing data. */
  source?: 'elation' | 'granerx'
}

/*
 * Pharmacy reception tracking - captures incoming confirmation state from GraneRx (filler side)
 */
export interface PharmacyReception {
  state: string // sent, cancelled, unknown (from ORC mapping)
  type: string // new_order, refill, change_order, discontinue, cancel
  detail: string // e.g. "GraneRx ORC.1=NW"
  occurredAt: string // ISO timestamp from HL7 MSH.7
}

// Structured PRN from GraneRx TQ1. TQ1.9=PRN is the flag. TQ1.9=P is not —
// GraneRx uses it on scheduled new orders — so when priority is P the
// as-needed signal lives in TQ1.3, suffixed onto the final frequency token in
// one of three spellings: PRP (Q6HPRP), AN (BIDAN, Q6HAN), or a bare P (QDP,
// BIDP, Q8HP). Across 300 sampled production orders a P or AN suffix agreed
// with an as-needed Elation sig 98% of the time, and no scheduled R order
// carried one.
const GRANERX_PRN_REPEAT_PATTERN = /(PRN|PRP|AN|P)$/

const TOPICAL_FORMS = new Set([
  'crm',
  'cream',
  'oint',
  'ointment',
  'gel',
  'lotion',
  'shampoo',
  'paste',
])

// Gel caps, gel capsules, and soft gels are oral capsules. Other semi-solid
// words in a name are topical.
const TOPICAL_NAME_PATTERN =
  /\b(cream|crm|ointment|oint|lotion|shampoo|paste)\b|(?<!soft[\s-])\bgel\b(?!\s*(caps?|capsules?)\b)/i

function trimmedLower(value: JsonValue | undefined): string {
  return typeof value === 'string' ? value.trim().toLowerCase() : ''
}

function asRecord(value: JsonValue | undefined): JsonObject | undefined {
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    return value as JsonObject
  }
  return undefined
}

export function isGraneRxPrnTiming(args: {
  repeatPattern?: string | null
  timingPriority?: string | null
}): boolean {
  if ((args.timingPriority ?? '').toUpperCase() === 'PRN') {
    return true
  }
  const lastToken = (args.repeatPattern ?? '')
    .toUpperCase()
    .trim()
    .split(/\s+/)
    .at(-1)
  return !!lastToken && GRANERX_PRN_REPEAT_PATTERN.test(lastToken)
}

/* ── Pharmacy matching utilities ──────────────────────────────────────── */

/** Maps pharmacyReception.type to a human-readable action label */
export function receptionTypeLabel(type: string): string {
  switch (type) {
    case 'new_order':
      return 'new prescription'
    case 'refill':
      return 'refill'
    case 'change_order':
      return 'prescription change'
    case 'discontinue':
      return 'discontinuation'
    case 'cancel':
      return 'cancellation'
    default:
      return 'order'
  }
}

export interface ReceptionTypesByTransmission {
  [transmissionType: string]: string[]
}

/**
 * Maps an Elation transmission type to the GraneRx reception types that would
 * confirm the pharmacy received that specific action. For example, a
 * `surescripts_cancel` error is only de-escalated by a GraneRx `cancel` or
 * `discontinue` reception — not by an earlier `refill`.
 */
export const TRANSMISSION_TO_RECEPTION_TYPES: ReceptionTypesByTransmission = {
  surescripts_cancel: ['cancel', 'discontinue'],
  surescripts: ['new_order', 'refill'],
  surescripts_change: ['change_order'],
}

/**
 * Maximum allowed time gap (in ms) between the GraneRx reception and the
 * Elation transmission error. Because GraneRx typically confirms *before*
 * Elation reports the Surescripts timeout, the reception timestamp can be
 * earlier than the transmission. This window allows matching in both
 * directions. GraneRx replies can arrive days late after an Elation timeout,
 * so the window is wide enough to catch them while still bounding stale
 * cross-cycle matches.
 */
export const RECEPTION_WINDOW_MS = 7 * 24 * 60 * 60 * 1000 // 7 days

// statusReason written when a MedicationRequest is stopped because a later order
// in the same Elation thread superseded it, rather than by a direct clinical stop.
// Both the sync fan-out and the stranded-active backfill write these.
export const THREAD_SUPERSESSION_STOP_REASON_DISCONTINUED =
  'Superseded and discontinued in Elation as part of the medication thread'
export const THREAD_SUPERSESSION_STOP_REASON_DOSE_CHANGE =
  'Superseded by a later dose in the Elation medication thread; only the current dose remains active'

// Shared prefix of both reasons above. A superseded member's stopDate is its
// successor's start, which for a same-day replacement equals its own order date —
// a real clinical date that consumers must not mistake for an unset/ambiguous one.
export const THREAD_SUPERSESSION_REASON_MARKER = 'Superseded'

export const GRANERX_DISCONTINUATION_ACTOR = Object.freeze({
  type: 'Organization',
  display: 'GraneRx',
}) satisfies Reference<Organization>

export function isGraneRxDiscontinuationActor(
  actor: Reference | undefined
): boolean {
  return (
    !!actor &&
    actor.type === 'Organization' &&
    actor.display === 'GraneRx' &&
    !actor.reference
  )
}

const GRANERX_DISCONTINUATION_REASONS = {
  cancelled: {
    system: MEDICATION_REQUEST_STATUS_REASON_SYSTEM,
    code: 'pharmacy-cancelled',
    display: 'Cancelled by pharmacy (GraneRx)',
  },
  stopped: {
    system: MEDICATION_REQUEST_STATUS_REASON_SYSTEM,
    code: 'pharmacy-discontinued',
    display: 'Discontinued by pharmacy (GraneRx)',
  },
} as const

/*
 * ##RefillUnavailableReason A refill copies the source order's stored payload,
 * so a source that is no longer the live order would re-prescribe a stopped
 * dose, quantity, or drug.
 */
export function refillSourceStatusReason(
  status: MedicationRequest['status'],
  threadDcDate: JsonValue | undefined
): string | null {
  if (
    status === 'stopped' ||
    status === 'cancelled' ||
    status === 'completed'
  ) {
    return `Order is ${status} — cannot create refill`
  }
  if (threadDcDate) {
    return 'Discontinued in Elation — cannot create refill'
  }
  return null
}

// Separator for medicationRecencyKey segments. Must sort BELOW every character a
// segment can contain (ISO digits/'-'/'T'/'Z' and hex id chars) so that an absent
// segment sorts first — i.e. "less information here" rather than "newer than
// everything". A ':' separator does the opposite: 0x3A outranks the ISO digits
// 0x30-0x39, so an empty segment jumps ABOVE every populated one and a later
// segment can silently override a decision the earlier segments already made.
const RECENCY_KEY_SEPARATOR = '!'

// Width the Elation med-order id segment is zero-padded to. Production ids are
// currently 15 and 16 digits, and these keys are compared lexically, so an
// unpadded 15-digit id would outrank a 16-digit one ('9...' > '1...'). 20 leaves
// headroom well past Elation's current issuance without needing a migration.
const RECENCY_KEY_ELATION_ID_WIDTH = 20

// Sort key placing an Elation thread member on the clinical timeline. Segments are
// compared lexically in order, each only breaking a tie in the one before it:
//
//  1. effectiveDate (startDate ?? authoredOn) — the true clinical timeline. Elation
//     stamps authoredOn from created_date, so a med documented during reconciliation
//     carries a newer authoredOn than the order it clinically predates.
//  2. authoredOn — same-day siblings share an effectiveDate (often date-only, no
//     time), so the full timestamp separates near-simultaneous re-issues. This only
//     does work when startDate is present; when it is absent, segment 1 has already
//     fallen back to authoredOn and the two segments agree.
//  3. Elation med-order id — settles members that tie on both dates. Elation issues
//     these monotonically, so it also reflects the order the two were written, and
//     unlike a Medplum resource id it exists on both a persisted member and an
//     inbound order still being built from the Elation payload.
//
// A fully dateless member keys as `!!<id>`, which sorts BELOW every dated member.
// Callers reading meds back out of Medplum, where a stored resource can lack both
// dates, should still skip dateless members rather than lean on that placement.
export function medicationRecencyKey(
  effectiveDate: string | undefined,
  authoredOn: string | undefined,
  elationId: number | string | undefined
): string {
  const sep = RECENCY_KEY_SEPARATOR
  const id =
    elationId === undefined
      ? ''
      : elationId.toString().padStart(RECENCY_KEY_ELATION_ID_WIDTH, '0')
  return `${effectiveDate ?? ''}${sep}${authoredOn ?? ''}${sep}${id}`
}

/**
 * Checks whether a GraneRx reception corresponds to an Elation transmission
 * error, by action type and temporal proximity.
 *
 * Returns true only when:
 * 1. Both transmission (error/failure) and reception exist
 * 2. The reception type matches the transmission type (e.g. cancel→cancel)
 * 3. The reception occurred within RECEPTION_WINDOW_MS of the transmission
 *    (in either direction — GraneRx often confirms before Elation errors)
 */
export function isReceptionMatchingTransmission(
  transmission: PharmacyTransmission | undefined,
  reception: PharmacyReception | undefined
): boolean {
  if (!transmission || !reception) {
    return false
  }

  if (transmission.state !== 'error' && transmission.state !== 'failure') {
    return false
  }

  const expectedTypes = TRANSMISSION_TO_RECEPTION_TYPES[transmission.type]
  if (!expectedTypes) {
    return false
  }
  if (!expectedTypes.includes(reception.type)) {
    return false
  }

  if (transmission.occurredAt && reception.occurredAt) {
    const txTime = new Date(transmission.occurredAt).getTime()
    const rxTime = new Date(reception.occurredAt).getTime()
    if (Math.abs(rxTime - txTime) > RECEPTION_WINDOW_MS) {
      return false
    }
  }

  return true
}

/*
 * Heuristic analysis types for medication supply and refill tracking
 */
export interface SupplyEstimate {
  estimatedEndDate: Date | null
  daysRemaining: number | null
  method: 'expectedSupplyDuration' | 'quantityAndDosage' | 'unknown'
  confidence: 'high' | 'medium' | 'low'
  disclaimer: string | null
}

export interface RefillStatus {
  repeatsAllowed: number | null
  refillsRemaining: number | null
  refillsUsed: number | null
  status: 'available' | 'exhausted' | 'unknown'
  label: string
}

export interface MedicationState {
  category:
    | 'active-current'
    | 'active-likely-finished'
    | 'active-expired'
    | 'active-outdated'
    | 'stopped'
    | 'cancelled'
    | 'completed'
    | 'unknown'
  label: string
  description: string
  requiresAction: boolean
  actionReason: string | null
}

export interface MedicationIssue {
  type:
    | 'pharmacy-transmission-error'
    | 'conflicting-active-orders'
    | 'refills-remaining-but-stopped'
    | 'active-past-validity'
    | 'missing-authored-date'
    | 'impossible-chronology'
    | 'active-very-old'
  severity: 'error' | 'warning' | 'info'
  message: string
}

export interface MedicationGroup {
  medicationName: string
  requests: ExtendedMedicationRequest[]
  /*
   * The request that represents the group on collapsed surfaces (the "Current
   * Medications" row link/date). When the group is active this is the
   * most-recent active request, NOT the most-recent request by effectiveDate — a
   * later completed Elation clinic-dispense must not become the link target and
   * make an actively-prescribed med read as "Completed."
   */
  representativeRequest: ExtendedMedicationRequest
  /*
   * The request `medicationState` was classified from. Collapsed rows that show
   * a date or link alongside the badge must read this, so the badge and the row
   * cannot describe different requests.
   */
  stateRequest: ExtendedMedicationRequest
  currentStatus: 'active' | 'stopped' | 'completed' | 'cancelled' | 'unknown'
  hasPharmacyError: boolean
  hasUnresolvedPharmacyError: boolean
  supplyEstimate: SupplyEstimate
  refillStatus: RefillStatus
  medicationState: MedicationState
  issues: MedicationIssue[]
}

interface MedicationLabel {
  display: string
  doseSummary?: string
}

export function medicationLabelForGroup(
  group: MedicationGroup
): MedicationLabel {
  const named = group.representativeRequest
  return {
    display: named.medicationDisplay ?? group.medicationName,
    doseSummary: named.doseSummary,
  }
}

export interface PrescriptionLabel {
  number: number
  count: number
  verb: 'Signed' | 'Recorded' | 'Drafted'
  time?: string
  orderType?: string
  statusLabel: string
  /* Stopped, completed, or cancelled (including by the pharmacy). An Active
   * sibling stays open: after a dose change the old strength can still be
   * filled (catalog #14). */
  isClosed: boolean
}

const CLOSED_STATUSES: ReadonlySet<string> = new Set([
  'stopped',
  'completed',
  'cancelled',
  'entered-in-error',
])

function orderTypeLabel(orderType: string | undefined): string | undefined {
  switch (orderType) {
    case 'New':
      return 'New'
    case 'Refill':
      return 'Refill'
    case 'DoseChange':
      return 'Dose change'
    default:
      return undefined
  }
}

function isUnsignedDraft(request: ExtendedMedicationRequest): boolean {
  return request.status === 'draft' && !request.signedDate
}

/*
 * When a prescription was signed, in epoch ms: the Elation signed_date, else
 * authoredOn (Elation created_date, or the day Orbit stored it). An unsigned
 * draft has no sign time yet and ranks after every signed prescription. A
 * prescription with neither date ranks first.
 */
function prescriptionSignInstant(request: ExtendedMedicationRequest): number {
  if (isUnsignedDraft(request)) {
    return Number.POSITIVE_INFINITY
  }
  const time = request.signedDate ?? request.authoredOn
  const parsed = time ? Date.parse(time) : Number.NaN
  return Number.isNaN(parsed) ? 0 : parsed
}

/*
 * ##PrescriptionLabel Numbers a medication's prescriptions by sign time, oldest
 * first, so each History line can say which prescription it belongs to. Equal
 * sign times fall back to recencyKey. The label also contains the sign time
 * because a number shifts when a prescription is added out of order or removed;
 * the time does not.
 */
export function prescriptionLabels(
  requests: ExtendedMedicationRequest[]
): Map<string, PrescriptionLabel> {
  const members = orderBy(
    [...new Map(requests.map((r) => [r.id, r])).values()].filter((r) => !!r.id),
    [prescriptionSignInstant, (r) => r.recencyKey],
    ['asc', 'asc']
  )
  return new Map(
    members.map((request, idx) => {
      const status = request.effectiveStatus
      let verb: PrescriptionLabel['verb'] = 'Signed'
      if (isUnsignedDraft(request)) {
        verb = 'Drafted'
      } else if (request.isDocumented) {
        verb = 'Recorded'
      }
      return [
        request.id as string,
        {
          number: idx + 1,
          count: members.length,
          verb,
          time: request.signedDate ?? request.authoredOn,
          orderType: request.orderType,
          statusLabel: status.label,
          isClosed: CLOSED_STATUSES.has(status.status),
        },
      ]
    })
  )
}

export function formatPrescriptionLabel(label: PrescriptionLabel): string {
  return compact([
    `Prescription ${label.number} of ${label.count}`,
    orderTypeLabel(label.orderType),
    label.time && `${label.verb} ${formatDateTime(label.time)}`,
    label.statusLabel,
  ]).join(' · ')
}

interface EffectiveMedicationStatus {
  status: string
  label: string
  overridden: boolean
}

export class ExtendedMedicationRequest extends Signable<MedicationRequest> {
  static readonly RESOURCE_TYPE = 'MedicationRequest'

  static readonly STATUS_LABELS: Record<MedicationRequest['status'], string> = {
    active: 'Active',
    stopped: 'Stopped',
    completed: 'Completed',
    cancelled: 'Cancelled',
    unknown: 'Unknown',
    'on-hold': 'On Hold',
    'entered-in-error': 'Entered in Error',
    draft: 'Draft',
  }

  static isInstance(value: unknown): value is MedicationRequest {
    return isResource(value) && isOfType(value, 'MedicationRequest')
  }

  static queryActiveByPatient(patient: Reference<Patient>): QueryTypes {
    return {
      patient,
      status: 'active',
      _sort: '-authoredon',
      _count: '100',
    }
  }

  // One search for many participants, replacing N `queryActiveByPatient`
  // calls. Page the result: `_count` bounds a page, not the match set.
  static queryActiveByPatients(patients: Reference<Patient>[]): QueryTypes {
    return {
      patient: patients,
      status: 'active',
      _sort: '-authoredon',
      _count: '1000',
    }
  }

  static queryOpenByPatient(patient: Reference<Patient>): QueryTypes {
    return {
      patient,
      status: 'active,on-hold',
      _sort: '-authoredon',
      _count: '100',
    }
  }

  static queryEncounterless(subject: string): QueryTypes {
    return {
      subject,
      'encounter:missing': 'true',
      'status:not': ['draft', 'entered-in-error'],
      _sort: '-_lastUpdated',
      _count: 100,
    }
  }

  static queryByEncounter(subject: string, encounter: string): QueryTypes {
    return {
      subject,
      encounter,
      'status:not': ['draft', 'entered-in-error'],
      _sort: '-_lastUpdated',
      _count: 100,
    }
  }

  static queryByIds(ids: string[]): QueryTypes {
    return {
      'status:not': 'entered-in-error',
      _sort: '-_lastUpdated',
      _id: ids,
      _count: ids.length,
    }
  }

  static queryStoppedWithoutEncounter(subject: string): QueryTypes {
    return {
      subject,
      status: 'stopped',
      'status:not': ['draft', 'entered-in-error'],
      '_tag:not': `${MEDICATION_REQUEST_STOPPED_ENCOUNTER_SYSTEM}|`,
      _sort: '-_lastUpdated',
      _count: 100,
    }
  }

  static queryStoppedByEncounter(
    subject: string,
    encounter: string
  ): QueryTypes {
    return {
      subject,
      status: 'stopped',
      'status:not': ['draft', 'entered-in-error'],
      _tag: `${MEDICATION_REQUEST_STOPPED_ENCOUNTER_SYSTEM}|${encounter}`,
      _sort: '-_lastUpdated',
      _count: 100,
    }
  }

  static queryAllBySubject(
    subject: string,
    { includeDraft = false }: { includeDraft?: boolean } = {}
  ): QueryTypes {
    return {
      subject,
      'status:not': includeDraft
        ? ['entered-in-error']
        : ['draft', 'entered-in-error'],
      _sort: '-_lastUpdated',
      _count: 1000,
    }
  }

  static queryByElationPrescriptionId(
    patient: Reference<Patient>,
    elationPrescriptionId: string
  ): QueryTypes {
    return {
      patient,
      identifier: identifier(ELATION_SANDBOX_ID_SYSTEM, elationPrescriptionId),
    }
  }

  static queryByGraneRxFillerOrderNumber(
    patient: Reference<Patient>,
    fillerOrderNumber: string
  ): QueryTypes {
    return {
      patient,
      identifier: identifier(GRANERX_FILLER_ORDER_SYSTEM, fillerOrderNumber),
    }
  }

  static buildPriorMap(
    medications: ExtendedMedicationRequest[]
  ): Map<string, ExtendedMedicationRequest> {
    const groups = ExtendedMedicationRequest.groupMedications(medications)
    return new Map(
      groups.flatMap((group) =>
        group.requests
          .slice(0, -1)
          .map((req, i) => [req.id, group.requests[i + 1]] as const)
      )
    )
  }

  get medication(): Reference<Medication> | undefined {
    return this.resource.medicationReference
  }

  get priorPrescription(): Reference<MedicationRequest> | undefined {
    return this.resource.priorPrescription
  }

  set priorPrescription(value: Reference<MedicationRequest> | undefined) {
    this.resource.priorPrescription = value
  }

  get status(): MedicationRequest['status'] {
    return this.resource.status
  }

  set status(value: MedicationRequest['status']) {
    this.resource.status = value
  }

  get statusText(): string {
    return ExtendedMedicationRequest.STATUS_LABELS[this.status] ?? this.status
  }

  /*
   * Derives a display status that considers both FHIR status and pharmacy reception.
   * When GraneRx confirms a cancellation but FHIR still says active, the effective
   * status reflects the pharmacy-confirmed state.
   */
  get effectiveStatus(): EffectiveMedicationStatus {
    const rxState =
      this.pharmacyReception?.state ??
      (this.pharmacyTransmission?.source === 'granerx'
        ? this.pharmacyTransmission.state
        : undefined)

    if (this.status === 'active' && rxState === 'cancelled') {
      return {
        status: 'cancelled',
        label: 'Cancelled by Pharmacy',
        overridden: true,
      }
    }
    return {
      status: this.status,
      label: this.statusText,
      overridden: false,
    }
  }

  get hasRecentGraneRxStop(): boolean {
    if (this.status !== 'stopped' && this.status !== 'cancelled') {
      return false
    }

    // pharmacyReception is replaced on each webhook.
    const receptionType = this.pharmacyReception?.type
    // granerxOrder.orderControl is that message's ORC.1, and it also covers
    // rows written before reception existed.
    const orderControl = this.granerxOrder?.orderControl
    return (
      receptionType === 'discontinue' ||
      receptionType === 'cancel' ||
      orderControl === 'DC' ||
      orderControl === 'CA'
    )
  }

  // A GraneRx stop no clinician is credited with: the pharmacy discontinued
  // this one order, not the Elation thread.
  get isPharmacyOnlyDiscontinuation(): boolean {
    const stoppedBy = this.discontinuedBy
    return (
      this.hasRecentGraneRxStop &&
      (stoppedBy == null || isGraneRxDiscontinuationActor(stoppedBy))
    )
  }

  setGraneRxDiscontinuation(status: 'stopped' | 'cancelled'): void {
    this.status = status
    this.discontinuedBy = GRANERX_DISCONTINUATION_ACTOR
    this.resource.statusReason = codeableConcept(
      GRANERX_DISCONTINUATION_REASONS[status]
    )
  }

  get startDate(): string | undefined {
    return this.resource.dosageInstruction?.find(
      (di) => di.timing?.repeat?.boundsPeriod?.start
    )?.timing?.repeat?.boundsPeriod?.start
  }

  set startDate(value: string | undefined) {
    _set(
      this.resource,
      'dosageInstruction.0.timing.repeat.boundsPeriod.start',
      value
    )
  }

  get stopDate(): string | undefined {
    return this.resource.dosageInstruction?.find(
      (di) => di.timing?.repeat?.boundsPeriod?.end
    )?.timing?.repeat?.boundsPeriod?.end
  }

  set stopDate(value: string | undefined) {
    _set(
      this.resource,
      'dosageInstruction.0.timing.repeat.boundsPeriod.end',
      value
    )
  }

  get refills(): number | undefined {
    return this.resource.dispenseRequest?.numberOfRepeatsAllowed
  }

  /*
   * Total refills authorized by the prescriber, as known to GraneRx (RXE.12).
   * Distinct from `refills` (Elation's auth_refills). Returns undefined when
   * no GraneRx HL7 has been processed for this MedicationRequest.
   */
  get refillsAuthorized(): number | undefined {
    const v = this.granerxOrder?.refillsAuthorized
    return typeof v === 'number' ? v : undefined
  }

  /*
   * Refills the pharmacy still has on file (RXE.16) — decremented as GraneRx
   * dispenses each fill. Undefined when no GraneRx data exists, which is the
   * primary signal that this is NOT a cycled medication. Cycle meds are the
   * ones the pharmacy auto-fills on a schedule, so GraneRx will always emit
   * RDE messages for them; one-off PRN/OTC orders won't.
   */
  get refillsRemaining(): number | undefined {
    const v = this.granerxOrder?.refillsRemaining
    return typeof v === 'number' ? v : undefined
  }

  /*
   * Pack start (ORC.15) for the pack GraneRx most recently reported — the
   * clock the cycle-refill tasking uses. Undefined until the next RF arrives
   * with a populated ORC.15; existing blobs predate this field entirely.
   */
  get cycleStart(): string | undefined {
    const v = this.granerxOrder?.cycleStart
    return typeof v === 'string' && v.length > 0 ? v : undefined
  }

  /*
   * TQ1.3 frequency code (QD, Q6HPRP, A1P QDAN, …). Undefined until a GraneRx
   * message with a TQ1 is stored (or the PRN backfill runs).
   */
  get repeatPattern(): string | undefined {
    const v = this.granerxOrder?.repeatPattern
    return typeof v === 'string' && v.length > 0 ? v : undefined
  }

  /*
   * TQ1.9 priority. Production is R / P / PRN. PRN is as-needed; P is not.
   */
  get timingPriority(): string | undefined {
    const v = this.granerxOrder?.timingPriority
    return typeof v === 'string' && v.length > 0 ? v : undefined
  }

  /*
   * Prefer GraneRx TQ1 when either timing field is present. Otherwise the
   * free-text dosage heuristic (Elation sig). Meds with no GraneRx blob stay
   * on the text path.
   */
  get isPrn(): boolean {
    if (this.repeatPattern !== undefined || this.timingPriority !== undefined) {
      return isGraneRxPrnTiming({
        repeatPattern: this.repeatPattern,
        timingPriority: this.timingPriority,
      })
    }
    return (this.dosageInstructions ?? []).some((text) =>
      isPrnText(text.toLowerCase())
    )
  }

  get intent(): MedicationRequest['intent'] {
    return this.resource.intent
  }

  set intent(value: MedicationRequest['intent']) {
    this.resource.intent = value
  }

  get medicationDisplay(): string | undefined {
    if (this.medication?.display) {
      return this.medication.display
    }

    return (
      this.resource.medicationCodeableConcept?.text ??
      displayFromCodeableConcept(this.resource.medicationCodeableConcept)
    )
  }

  get medicationCode(): string | undefined {
    return codeFromCodeableConcept(this.resource.medicationCodeableConcept)
  }

  get subject(): Reference | undefined {
    return this.resource.subject
  }

  set subject(value: MedicationRequest['subject']) {
    this.resource.subject = value
  }

  get authoredOn(): string | undefined {
    return this.resource.authoredOn
  }

  set authoredOn(value: string | undefined) {
    this.resource.authoredOn = value
  }

  // Recency key for ordering within a medication thread. Prefers the clinical
  // start date over authoredOn because Elation stamps authoredOn from
  // created_date — the day a med was keyed in — so a documented/downloaded med
  // recorded during a reconciliation gets a "newer" authoredOn than the
  // actively-prescribed order it predates. startDate (boundsPeriod.start)
  // carries the true fill/start date and equals authoredOn for normal orders.
  get effectiveDate(): string | undefined {
    return this.startDate ?? this.authoredOn
  }

  get medicationReference(): Reference | undefined {
    return this.resource.medicationReference
  }

  set medication(value: Medication) {
    this.resource.medicationReference = createReference(value)
    if (value.code) {
      this.resource.medicationCodeableConcept = value.code
    } else if (value.text?.div) {
      this.resource.medicationCodeableConcept = {
        text: value.text.div,
      }
    }
  }

  get requester(): Reference | undefined {
    return this.resource.requester
  }

  set requester(value: MedicationRequest['requester']) {
    this.resource.requester = value
  }

  get requesterDisplay(): string | undefined {
    return this.resource.requester?.display
  }

  get dispenseRequest(): MedicationRequest['dispenseRequest'] | undefined {
    return this.resource.dispenseRequest
  }

  set dispenseRequest(value: MedicationRequest['dispenseRequest']) {
    this.resource.dispenseRequest = value
  }

  get reasonCode(): CodeableConcept[] | undefined {
    return this.resource.reasonCode
  }

  set reasonCode(value: CodeableConcept[] | undefined) {
    this.resource.reasonCode = value
  }

  get dosageInstructions(): string[] | undefined {
    return this.resource.dosageInstruction?.map((d) =>
      compact([d.text, codeFromCodeableConcept(d.route)]).join(' ')
    )
  }

  set dosageInstructions(text: string | undefined) {
    _set(this.resource, 'dosageInstruction.0.text', text)
  }

  // Dose and frequency abbreviated for a one-line row ("1 tab QD"), or
  // undefined when the sig yields neither. Structured as-needed is included
  // even when the sig text never says PRN. Two dosage instructions are two
  // regimens; a one-line label cannot show both, so it is declined.
  get doseSummary(): string | undefined {
    const instructions = this.resource.dosageInstruction
    if (instructions == null || instructions.length !== 1) {
      return undefined
    }
    const instruction = instructions[0]
    const hasTiming =
      this.repeatPattern !== undefined || this.timingPriority !== undefined
    return formatSigSummary(
      instruction?.text,
      hasTiming
        ? { authoritativeAsNeeded: this.isPrn }
        : {
            asNeeded:
              instruction?.asNeededBoolean === true ||
              instruction?.asNeededCodeableConcept != null,
          }
    )
  }

  get categoryCodes(): string[] | undefined {
    return this.resource.category?.map((c) => codeFromCodeableConcept(c) ?? '')
  }

  get priority(): MedicationRequest['priority'] {
    return this.resource.priority
  }

  get noteSummary(): string | undefined {
    return this.resource.note?.map((n) => n.text).join(', ')
  }

  get statusReason(): string | undefined {
    return this.resource.statusReason?.text
  }

  set statusReason(value: string | undefined) {
    _set(this.resource, 'statusReason.text', value)
  }

  get encounter(): Reference<Encounter> | undefined {
    return this.resource.encounter
  }

  set encounter(value: MedicationRequest['encounter']) {
    this.resource.encounter = value
  }

  /*
   * Get stable medication name from medicationCodeableConcept.text
   * This is more reliable than medicationDisplay which may have placeholder
   * reference strings in early versions before backfill scripts ran
   */
  get medicationName(): string | undefined {
    return (
      this.resource.medicationCodeableConcept?.text ?? this.medicationDisplay
    )
  }

  /*
   * Check if this is a documented medication (patient is taking but not prescribed by us)
   * vs an ordered prescription (sent to pharmacy)
   */
  get isDocumented(): boolean {
    const order = this.parsedExternalOrder
    if (!order) {
      return false
    }
    return order.is_doc_med === true
  }

  /*
   * Semi-solids are used in no fixed amount, so pack math cannot predict
   * run-out. Patches are scheduled and stay eligible. A missing or malformed
   * Elation blob is not topical; an empty form falls back to the name.
   */
  get isTopical(): boolean {
    const order = asRecord(this.parsedExternalOrder)
    if (!order) {
      return false
    }
    const medication = asRecord(order.medication)
    const form = trimmedLower(medication?.form)
    const route = trimmedLower(medication?.route)
    if (form.startsWith('patch')) {
      return false
    }
    // A missing form still leaves route topical/external, which would skip a
    // scheduled patch whose name is the only signal.
    if (/\bpatch\b/i.test(this.medicationName ?? '')) {
      return false
    }
    if (route === 'topical' || route === 'external') {
      return true
    }
    if (TOPICAL_FORMS.has(form)) {
      return true
    }
    if (form === '') {
      return TOPICAL_NAME_PATTERN.test(this.medicationName ?? '')
    }
    return false
  }

  @Extension({
    url: MEDICATION_REQUEST_TYPE_SYSTEM,
    kind: 'valueString',
  })
  _type?: MedicationRequestType

  get type(): MedicationRequestType | undefined {
    return this._type
  }

  set type(value: MedicationRequestType) {
    this._type = value
    // https://seenhealth.slack.com/archives/C078NUJP6B1/p1758222798474519
    // https://build.fhir.org/ig/HL7/US-Core/medication-list.html
    if (value === MedicationRequestType.OverTheCounter) {
      this.intent = 'plan'
    }
  }

  /*
   * Comprehensive pharmacy transmission tracking - captures state, type, detail, and timestamp
   */
  @Extension({
    url: MEDICATION_REQUEST_PHARMACY_TRANSMISSION_SYSTEM,
    kind: 'valueString',
  })
  _pharmacyTransmission?: string

  get pharmacyTransmission(): PharmacyTransmission | undefined {
    return this._pharmacyTransmission
      ? JSON.parse(this._pharmacyTransmission)
      : undefined
  }

  set pharmacyTransmission(value: PharmacyTransmission | undefined) {
    this._pharmacyTransmission = value ? JSON.stringify(value) : undefined
  }

  /*
   * Legacy pharmacy transmission issue tracking (backward compatibility)
   * Derives from pharmacyTransmission when state is 'failure' or 'error'
   */
  @Extension({
    url: MEDICATION_REQUEST_PHARMACY_TRANSMISSION_ISSUE_SYSTEM,
    kind: 'valueString',
  })
  _pharmacyTransmissionIssue?: string

  get pharmacyTransmissionIssue():
    | { occurredAt: string; issue: string }
    | undefined {
    /* First check legacy field for backward compatibility with old data */
    if (this._pharmacyTransmissionIssue) {
      return JSON.parse(this._pharmacyTransmissionIssue)
    }

    /* Derive from new comprehensive transmission data */
    const transmission = this.pharmacyTransmission
    if (!transmission) {
      return undefined
    }

    /* Only expose as "issue" if state indicates a problem AND it's a known electronic transmission */
    if (
      (transmission.state === 'failure' || transmission.state === 'error') &&
      (transmission.type.startsWith('surescripts') ||
        transmission.source === 'granerx')
    ) {
      /* Elation flags Surescripts errors when GraneRx hasn't replied in
       * its window. GraneRx often does reply, just late — and Elation
       * never retracts. Suppress the stale error when we have a matching
       * GraneRx reception (right action type, within the 7-day window). */
      if (
        isReceptionMatchingTransmission(transmission, this.pharmacyReception)
      ) {
        return undefined
      }
      return {
        occurredAt: transmission.occurredAt,
        issue: transmission.detail
          ? `${transmission.state}: ${transmission.detail}`
          : transmission.state,
      }
    }

    return undefined
  }

  set pharmacyTransmissionIssue(
    value: { occurredAt: string; issue: string } | undefined
  ) {
    this._pharmacyTransmissionIssue = value ? JSON.stringify(value) : undefined
  }

  @Extension({
    url: MEDICATION_REQUEST_PHARMACY_TRANSMISSION_ISSUE_RESOLVED_AT_SYSTEM,
    kind: 'valueString',
  })
  pharmacyTransmissionIssueResolvedAt?: string

  @Tag({
    system: MEDICATION_REQUEST_STOPPED_ENCOUNTER_SYSTEM,
  })
  _stoppedEncounter?: Coding

  get stoppedEncounter(): Reference<Encounter> | undefined {
    return parseReferenceString(
      this.getTag(MEDICATION_REQUEST_STOPPED_ENCOUNTER_SYSTEM)?.code
    )
  }

  set stoppedEncounter(value: Reference<Encounter> | undefined) {
    if (!value) {
      this.removeTag({ system: MEDICATION_REQUEST_STOPPED_ENCOUNTER_SYSTEM })
    } else {
      this.setTag(MEDICATION_REQUEST_STOPPED_ENCOUNTER_SYSTEM, value.reference)
    }
  }

  @Extension({
    url: MEDICATION_REQUEST_DISCONTINUED_BY_SYSTEM,
    kind: 'valueReference',
  })
  _discontinuedBy?: Reference<Practitioner | Organization>

  get discontinuedBy(): Reference<Practitioner | Organization> | undefined {
    return this._discontinuedBy
  }

  set discontinuedBy(
    value: Reference<Practitioner | Organization> | undefined
  ) {
    this._discontinuedBy = value
  }

  /*
   * ========================================
   * ELATION MEDICATION THREAD METADATA
   * ========================================
   * Tracking Elation medication thread relationships.
   * Thread groups all related requests (New + Refills + DoseChanges) into one series.
   *
   * threadId uses meta.tag for efficient querying.
   * orderType and isPermanent use extensions.
   */

  @Extension({
    url: MEDICATION_REQUEST_ORDER_TYPE_SYSTEM,
    kind: 'valueString',
  })
  orderType?: string

  @Extension({
    url: MEDICATION_REQUEST_IS_PERMANENT_SYSTEM,
    kind: 'valueBoolean',
  })
  isPermanent?: boolean

  @Extension({
    url: MEDICATION_REQUEST_GRANERX_ORDER_SYSTEM,
    kind: 'valueString',
  })
  _granerxOrder?: string

  get granerxOrder(): JsonObject | null {
    if (!this._granerxOrder) {
      return null
    }
    try {
      return JSON.parse(this._granerxOrder)
    } catch {
      return null
    }
  }

  set granerxOrder(value: JsonObject | null) {
    this._granerxOrder = value ? JSON.stringify(value) : undefined
  }

  /*
   * Pharmacy reception tracking — incoming confirmation from GraneRx (filler side).
   * Separate from pharmacyTransmission (Elation/placer side) to enable concordance checking.
   */
  @Extension({
    url: MEDICATION_REQUEST_PREVIOUS_DISPENSER_SYSTEM,
    kind: 'valueString',
  })
  previousDispenser?: string

  @Extension({
    url: MEDICATION_REQUEST_PHARMACY_RECEPTION_SYSTEM,
    kind: 'valueString',
  })
  _pharmacyReception?: string

  get pharmacyReception(): PharmacyReception | undefined {
    return this._pharmacyReception
      ? JSON.parse(this._pharmacyReception)
      : undefined
  }

  set pharmacyReception(value: PharmacyReception | undefined) {
    this._pharmacyReception = value ? JSON.stringify(value) : undefined
  }

  /*
   * Whether the pharmacy (GraneRx) has confirmed receipt of this prescription.
   * Checks both the new pharmacyReception field and legacy pharmacyTransmission with source='granerx'.
   */
  get isPharmacyConfirmed(): boolean {
    if (this.pharmacyReception?.state === 'sent') {
      return true
    }
    // Backward compat: old data stored GraneRx confirmations in pharmacyTransmission
    const tx = this.pharmacyTransmission
    return tx?.source === 'granerx' && tx.state === 'sent'
  }

  /**
   * Sets the GraneRx filler order number as a top-level identifier,
   * enabling Medplum search by filler order number.
   */
  set granerxFillerOrderNumber(value: string | undefined) {
    if (value) {
      this.addIdentifier(GRANERX_FILLER_ORDER_SYSTEM, value, {
        type: identifierTypeFiller,
      })
    }
  }

  get granerxFillerOrderNumber(): string | undefined {
    return this.identifiers(GRANERX_FILLER_ORDER_SYSTEM)[0]?.value
  }

  /* Cached parse of externalResourceJSON to avoid re-parsing on every access */
  private _parsedExternalOrder?: JsonObject | null

  private get parsedExternalOrder(): JsonObject | null {
    if (this._parsedExternalOrder !== undefined) {
      return this._parsedExternalOrder
    }
    if (!this.externalResourceJSON) {
      this._parsedExternalOrder = null
      return null
    }
    try {
      this._parsedExternalOrder = JSON.parse(this.externalResourceJSON)
    } catch {
      this._parsedExternalOrder = null
    }
    return this._parsedExternalOrder ?? null
  }

  /*
   * ##RefillUnavailableReason Returns a reason string if this medication can't be refilled, or null if it can.
   * Each check mirrors a backend validation in prescribe.ts so users see a
   * disabled button + tooltip instead of hitting a 422 error.
   */
  get refillUnavailableReason(): string | null {
    const order = this.parsedExternalOrder
    if (!order) {
      return this.externalResourceJSON
        ? 'Invalid medication data — cannot create refill'
        : 'Not an Elation-sourced medication'
    }
    if (!(order.thread as JsonObject | undefined)?.id) {
      return 'Missing medication thread — cannot create refill'
    }
    if (!(order.medication as JsonObject | undefined)?.id) {
      return 'Missing medication identifier — cannot create refill'
    }
    if (order.is_doc_med) {
      return 'Documented medication — not prescribed'
    }
    if (!order.prescribing_physician) {
      return 'Missing prescribing physician — cannot create refill'
    }
    const sourceUnavailable = refillSourceStatusReason(
      this.status,
      (order.thread as JsonObject | undefined)?.dc_date
    )
    if (sourceUnavailable) {
      return sourceUnavailable
    }
    return null
  }

  /**
   * Get the Elation thread ID from meta.tag.
   * Thread ID links all related medication requests (New + Refills + DoseChanges).
   */
  get threadId(): string | undefined {
    return this.getTag(MEDICATION_REQUEST_THREAD_ID_SYSTEM)?.code
  }

  /**
   * Set the Elation thread ID using meta.tag.
   * This enables efficient searching via _tag parameter.
   */
  set threadId(value: string | undefined) {
    if (!value) {
      this.removeTag({ system: MEDICATION_REQUEST_THREAD_ID_SYSTEM })
    } else {
      this.setTag(MEDICATION_REQUEST_THREAD_ID_SYSTEM, value)
    }
  }

  private get storedElationThreadId(): string | undefined {
    const tagged = this.threadId
    if (tagged) {
      return tagged
    }
    const thread = this.parsedExternalOrder?.thread
    if (typeof thread !== 'object' || thread === null) {
      return undefined
    }
    const id = (thread as { id?: unknown }).id
    if (typeof id !== 'string' && typeof id !== 'number') {
      return undefined
    }
    const value = id.toString()
    return value === '' ? undefined : value
  }

  /*
   * ========================================
   * HEURISTIC ANALYSIS METHODS
   * ========================================
   * Static methods for analyzing medication supply, refills, and state.
   * These methods take an array of MedicationRequests (all requests for a given medication)
   * and internally find the most recent request for analysis.
   */

  /**
   * Get the most recent MedicationRequest from an array, sorted by authoredOn date.
   * Returns undefined if array is empty.
   */
  private static getMostRecent<T extends { authoredOn?: string }>(
    requests: T[]
  ): T | undefined {
    if (requests.length === 0) {
      return undefined
    }

    return orderBy(requests, (request) => request.authoredOn ?? '', ['desc'])[0]
  }

  /*
   * ##MedicationRepresentative The request that defines what the participant is
   * currently on. Picking the newest request is status-blind: Elation pairs a
   * live prescription with same-RxNorm one-time dispenses (status=completed)
   * authored later, so the newest request is routinely not the one being taken.
   * Members rank on the clinical timeline (recencyKey), not authoredOn:
   * Elation stamps authoredOn from created_date, so a regimen documented
   * during reconciliation is often keyed after the order being taken.
   *
   * The latest active member that has started wins; an active member whose
   * start is still ahead (a scheduled taper step or next course) does not
   * replace today's order, and leads only when no active member has started
   * (then the soonest start day wins, and the latest member on that day).
   * A stopped or cancelled member overrides it only through `endsThreadOver`,
   * in which case that discontinuation defines the group — not whatever was
   * authored last, which may be a one-time dispense handed out after the stop.
   * A GraneRx discontinue of one order defines the group only when Elation
   * also discontinued the thread.
   * With no active member, the latest member on the timeline wins.
   * `groupMedications` (status and row), `classifyMedicationState` (badge),
   * and `medicationLabelForGroup` (row label) all read this one request.
   */
  static getRepresentative<
    T extends MedicationRequest | ExtendedMedicationRequest,
  >(requests: T[]): T | undefined {
    const members = requests.map((request) => ({
      request,
      extended:
        request instanceof ExtendedMedicationRequest
          ? request
          : ExtendedMedicationRequest.fromFhirResource(request),
    }))
    const actives = members.filter(
      ({ extended }) => extended.status === 'active'
    )
    if (actives.length === 0) {
      return orderBy(members, ({ extended }) => extended.recencyKey, [
        'desc',
      ])[0]?.request
    }

    const currentDay = today()
    const startDay = ({ extended }: (typeof members)[number]): string =>
      (extended.effectiveDate ?? '').slice(0, 10)
    const started = actives.filter((member) => startDay(member) <= currentDay)
    const latestActive =
      started.length > 0
        ? orderBy(started, ({ extended }) => extended.recencyKey, ['desc'])[0]
        : orderBy(
            actives,
            [startDay, ({ extended }) => extended.recencyKey],
            ['asc', 'desc']
          )[0]
    const latestActiveAuthoredOn = orderBy(
      actives.map(({ extended }) => extended.authoredOn ?? ''),
      (authoredOn) => authoredOn,
      ['desc']
    )[0]

    const supersedingDiscontinuation = orderBy(
      members.filter(({ extended }) =>
        ExtendedMedicationRequest.endsThreadOver(
          extended,
          latestActive.extended,
          latestActiveAuthoredOn
        )
      ),
      ({ extended }) => extended.authoredOn ?? '',
      ['desc']
    )[0]

    return (supersedingDiscontinuation ?? latestActive).request
  }

  /*
   * ##MedicationRepresentative Whether a stopped or cancelled member defines the
   * thread over its latest active member. Neither date alone decides it:
   *
   * - authoredOn is the day Elation keyed the order. A historical course
   *   documented during reconciliation carries the newest authoredOn while
   *   sitting first on the clinical timeline, and the stranded-active backfill
   *   stops it as "superseded by a later dose". That bookkeeping stop must not
   *   hide the dose that superseded it.
   * - effectiveDate (startDate ?? authoredOn) misses real stops: a stop
   *   inherits the medication's original boundsPeriod.start, so its
   *   effectiveDate can predate the active order it ended.
   *
   * - Supersession stops (either `THREAD_SUPERSESSION_*` reason) are written
   *   only on members at or before the thread's clinical latest, and an active
   *   member newer than a thread discontinuation is a re-prescription that is
   *   deliberately left open. Their authoredOn says nothing about the thread.
   *
   * - A GraneRx discontinue with no clinician behind it stops one order, often
   *   a duplicate refill, while the Elation thread stays live. Neither its
   *   recencyKey nor its authoredOn says the medication was stopped.
   *
   * So a stop wins when Elation discontinued the thread (`thread.dc_date`) on
   * or after the active order started. Otherwise, unless it is a pharmacy-only
   * stop, it wins when it is newer on the clinical timeline (recencyKey) or
   * when it was authored after every active member and is not a supersession
   * stop. The authoredOn rule compares against the
   * latest-authored active member rather than the representative: a stop
   * keyed before a later-documented active course does not end the thread.
   * An undated active member keys below every
   * dated one, so any dated stop still defines the group: showing a
   * possibly-stopped medication as Active is the more dangerous error.
   */
  private static endsThreadOver(
    candidate: ExtendedMedicationRequest,
    active: ExtendedMedicationRequest,
    latestActiveAuthoredOn: string
  ): boolean {
    if (candidate.status !== 'stopped' && candidate.status !== 'cancelled') {
      return false
    }
    const threadDcDate = asRecord(
      candidate.parsedExternalOrder?.thread
    )?.dc_date
    if (
      typeof threadDcDate === 'string' &&
      threadDcDate.slice(0, 10) >= (active.effectiveDate ?? '').slice(0, 10)
    ) {
      return true
    }
    if (candidate.isPharmacyOnlyDiscontinuation) {
      return false
    }
    if (candidate.recencyKey > active.recencyKey) {
      return true
    }
    return (
      !candidate.statusReason?.startsWith(THREAD_SUPERSESSION_REASON_MARKER) &&
      (candidate.authoredOn ?? '') > latestActiveAuthoredOn
    )
  }

  /**
   * Estimate when the medication supply will end based on available data.
   * Uses expectedSupplyDuration if present, otherwise attempts to parse dosage instructions.
   *
   * @param requests - All MedicationRequests for this medication, will use most recent
   */
  static estimateSupplyEnd(requests: MedicationRequest[]): SupplyEstimate {
    const mostRecent = ExtendedMedicationRequest.getMostRecent(requests)
    if (!mostRecent) {
      return {
        estimatedEndDate: null,
        daysRemaining: null,
        method: 'unknown',
        confidence: 'low',
        disclaimer: 'No medication requests available',
      }
    }

    const authoredOn = mostRecent.authoredOn
    if (!authoredOn) {
      return {
        estimatedEndDate: null,
        daysRemaining: null,
        method: 'unknown',
        confidence: 'low',
        disclaimer: 'Cannot estimate - prescription date unavailable',
      }
    }

    const startDate = parseISO(authoredOn)
    const now = new Date()

    /* Method 1: Use expectedSupplyDuration if present */
    const supplyDuration =
      mostRecent.dispenseRequest?.expectedSupplyDuration?.value
    if (supplyDuration) {
      const endDate = new Date(startDate)
      endDate.setDate(endDate.getDate() + supplyDuration)

      return {
        estimatedEndDate: endDate,
        daysRemaining: differenceInDays(endDate, now),
        method: 'expectedSupplyDuration',
        confidence: 'high',
        disclaimer: null,
      }
    }

    /* Method 2: Try to parse quantity and dosage */
    const quantity = mostRecent.dispenseRequest?.quantity?.value
    const extendedMostRecent =
      ExtendedMedicationRequest.fromFhirResource(mostRecent)
    const dosageText = extendedMostRecent.dosageInstructions?.[0]

    if (quantity && dosageText) {
      const parsed = parseDailyDoseFromText(dosageText)
      if (parsed.dose != null && parsed.dose > 0) {
        const daysSupply = Math.floor(quantity / parsed.dose)
        const endDate = new Date(startDate)
        endDate.setDate(endDate.getDate() + daysSupply)

        let overallConfidence: 'high' | 'medium' | 'low' = 'medium'
        let disclaimer: string | null =
          'Supply estimate based on parsed dosage; actual usage may vary'

        if (parsed.confidence === 'high') {
          overallConfidence = 'high'
          disclaimer = null
        } else if (parsed.confidence === 'low') {
          overallConfidence = 'low'
          disclaimer =
            'Supply estimate uncertain - dosage frequency could not be parsed'
        }

        if (parsed.isPrn) {
          disclaimer =
            'Supply estimate is a maximum based on the stated schedule; as-needed use may last longer'
        }

        return {
          estimatedEndDate: endDate,
          daysRemaining: differenceInDays(endDate, now),
          method: 'quantityAndDosage',
          confidence: overallConfidence,
          disclaimer,
        }
      }
    }

    /* Cannot estimate */
    return {
      estimatedEndDate: null,
      daysRemaining: null,
      method: 'unknown',
      confidence: 'low',
      disclaimer: 'Duration unknown - insufficient data to estimate supply',
    }
  }

  /**
   * Analyze refill status from medication request.
   *
   * Returns simple status based on authorized refills only.
   * Does NOT attempt to calculate remaining refills or refills used,
   * as these would be unreliable estimates based on time elapsed.
   *
   * @param requests - All MedicationRequests for this medication, will use most recent
   */
  static analyzeRefillStatus(requests: MedicationRequest[]): RefillStatus {
    const mostRecent = ExtendedMedicationRequest.getMostRecent(requests)
    if (!mostRecent) {
      return {
        repeatsAllowed: null,
        refillsRemaining: null,
        refillsUsed: null,
        status: 'unknown',
        label: 'No medication requests available',
      }
    }

    const repeatsAllowed = mostRecent.dispenseRequest?.numberOfRepeatsAllowed

    if (repeatsAllowed === undefined || repeatsAllowed === null) {
      return {
        repeatsAllowed: null,
        refillsRemaining: null,
        refillsUsed: null,
        status: 'unknown',
        label: 'Refill information unavailable',
      }
    }

    /* Simple status based on authorized refills only */
    if (repeatsAllowed === 0) {
      return {
        repeatsAllowed: 0,
        refillsRemaining: null,
        refillsUsed: null,
        status: 'unknown',
        label: 'No refills authorized',
      }
    }

    return {
      repeatsAllowed,
      refillsRemaining: null,
      refillsUsed: null,
      status: 'available',
      label: `${repeatsAllowed} ${repeatsAllowed === 1 ? 'refill' : 'refills'} authorized`,
    }
  }

  /**
   * Classify medication state from the group's representative request.
   * This drives the primary visual indicator.
   *
   * @param requests - All MedicationRequests for this medication
   */
  static classifyMedicationState(
    requests: MedicationRequest[]
  ): MedicationState {
    const representative = ExtendedMedicationRequest.getRepresentative(requests)
    if (!representative) {
      return {
        category: 'unknown',
        label: 'Unknown',
        description: 'No medication requests available',
        requiresAction: false,
        actionReason: null,
      }
    }

    const status = representative.status

    /* Explicitly stopped, cancelled, or completed */
    if (status === 'stopped') {
      const refillStatus =
        ExtendedMedicationRequest.analyzeRefillStatus(requests)
      const hasRefillsLeft =
        refillStatus.status === 'available' &&
        refillStatus.refillsRemaining &&
        refillStatus.refillsRemaining > 0

      return {
        category: 'stopped',
        label: 'Stopped',
        description:
          representative.statusReason?.text ?? 'Discontinued by provider',
        requiresAction: false,
        actionReason: hasRefillsLeft
          ? 'Medication stopped but has unused refills'
          : null,
      }
    }

    if (status === 'cancelled') {
      return {
        category: 'cancelled',
        label: 'Cancelled',
        description:
          representative.statusReason?.text ?? 'Cancelled before dispensing',
        requiresAction: false,
        actionReason: null,
      }
    }

    if (status === 'completed') {
      return {
        category: 'completed',
        label: 'Completed',
        description: 'Treatment course finished',
        requiresAction: false,
        actionReason: null,
      }
    }

    /* Active medications - need nuanced classification */
    if (status === 'active') {
      /* Check validity period first */
      const validityEnd = representative.dispenseRequest?.validityPeriod?.end
      if (validityEnd) {
        const validityEndDate = parseISO(validityEnd)
        const now = new Date()
        if (now > validityEndDate) {
          return {
            category: 'active-expired',
            label: 'Active (Expired)',
            description: `Validity ended ${format(validityEndDate, 'MMM d, yyyy')}`,
            requiresAction: true,
            actionReason: 'Active order past validity period - review needed',
          }
        }
      }

      /* Active, no issues detected */
      return {
        category: 'active-current',
        label: 'Active',
        description: 'Active medication',
        requiresAction: false,
        actionReason: null,
      }
    }

    /* Unknown or other status */
    return {
      category: 'unknown',
      label: status ?? 'Unknown',
      description: 'Status unclear',
      requiresAction: false,
      actionReason: null,
    }
  }

  /**
   * Detect issues with a medication request.
   * Returns array of issues for display.
   *
   * @param requests - All MedicationRequests for this medication, will use most recent
   */
  static detectIssues(requests: MedicationRequest[]): MedicationIssue[] {
    const mostRecent = ExtendedMedicationRequest.getMostRecent(requests)
    if (!mostRecent) {
      return []
    }

    const issues: MedicationIssue[] = []
    const medicationState =
      ExtendedMedicationRequest.classifyMedicationState(requests)
    const refillStatus = ExtendedMedicationRequest.analyzeRefillStatus(requests)

    /*
     * Pharmacy transmission errors - checks comprehensive transmission data
     * Issues are detected when state is 'failure' or 'error' and not yet resolved
     */
    const extendedMostRecent =
      ExtendedMedicationRequest.fromFhirResource(mostRecent)
    if (
      extendedMostRecent.pharmacyTransmissionIssue &&
      !extendedMostRecent.pharmacyTransmissionIssueResolvedAt
    ) {
      issues.push({
        type: 'pharmacy-transmission-error',
        severity: 'error',
        message: extendedMostRecent.pharmacyTransmissionIssue.issue,
      })
    }

    /* Refills remaining but medication stopped */
    if (
      medicationState.category === 'stopped' &&
      refillStatus.status === 'available' &&
      refillStatus.refillsRemaining &&
      refillStatus.refillsRemaining > 0
    ) {
      issues.push({
        type: 'refills-remaining-but-stopped',
        severity: 'warning',
        message: `Medication stopped with ${refillStatus.refillsRemaining} unused ${refillStatus.refillsRemaining === 1 ? 'refill' : 'refills'}`,
      })
    }

    /* Active past validity period */
    if (medicationState.category === 'active-expired') {
      issues.push({
        type: 'active-past-validity',
        severity: 'error',
        message: 'Active order past validity period',
      })
    }

    /* Missing authored date */
    if (!mostRecent.authoredOn) {
      issues.push({
        type: 'missing-authored-date',
        severity: 'warning',
        message: 'Prescription date unavailable',
      })
    }

    return issues
  }

  /**
   * Requests share a cluster when they are the same drug or one continues the
   * other.
   *
   * Same drug means the same RxNorm code. A continuation is a
   * `priorPrescription` link. That link carries through: if A continues B, and
   * B is the same drug as C, then A, B, and C are one cluster.
   *
   * A request with no id is left out.
   */
  private static clusterByRxNormOrPriorPrescription(
    medications: ExtendedMedicationRequest[]
  ): ExtendedMedicationRequest[][] {
    const parent = new Map<string, string>()

    const find = (id: string): string => {
      if (!parent.has(id)) {
        parent.set(id, id)
        return id
      }
      const p = parent.get(id)
      if (p === id || !p) {
        return id
      }
      const root = find(p)
      parent.set(id, root)
      return root
    }

    const union = (id1: string, id2: string): void => {
      const root1 = find(id1)
      const root2 = find(id2)
      if (root1 !== root2) {
        parent.set(root2, root1)
      }
    }

    for (const med of medications) {
      if (med.id) {
        find(med.id)
      }
    }

    for (const med of medications) {
      if (med.id && med.priorPrescription) {
        const priorId = resolveId(med.priorPrescription)
        if (priorId && medications.some((m) => m.id === priorId)) {
          union(med.id, priorId)
        }
      }
    }

    const rxnormGroups = new Map<string, string[]>()
    for (const med of medications) {
      if (!med.id) {
        continue
      }
      const code = codeFromCodeableConcept(
        med.resource.medicationCodeableConcept,
        RX_NORM_SYSTEM
      )
      if (!code) {
        continue
      }
      const group = rxnormGroups.get(code) ?? []
      group.push(med.id)
      rxnormGroups.set(code, group)
    }

    for (const ids of rxnormGroups.values()) {
      const first = ids[0]
      if (!first || ids.length < 2) {
        continue
      }
      for (let i = 1; i < ids.length; i++) {
        const id = ids[i]
        if (id) {
          union(first, id)
        }
      }
    }

    const groups = new Map<string, ExtendedMedicationRequest[]>()
    for (const med of medications) {
      if (!med.id) {
        continue
      }
      const root = find(med.id)
      const group = groups.get(root) ?? []
      group.push(med)
      groups.set(root, group)
    }

    return Array.from(groups.values())
  }

  /*
   * Elation's thread is one prescription lineage. A second thread for the same
   * RxNorm is a second prescription (a PRN beside a scheduled dose, or a later
   * antibiotic course) and must stay its own cluster. Requests with no stored
   * thread id fall back to RxNorm among themselves and do not join a thread.
   */
  private static clusterByElationThread(
    medications: ExtendedMedicationRequest[]
  ): ExtendedMedicationRequest[][] {
    const byThread = new Map<string, ExtendedMedicationRequest[]>()
    const unthreaded: ExtendedMedicationRequest[] = []

    for (const med of medications) {
      if (!med.id) {
        continue
      }
      const threadId = med.storedElationThreadId
      if (!threadId) {
        unthreaded.push(med)
        continue
      }
      const existing = byThread.get(threadId) ?? []
      existing.push(med)
      byThread.set(threadId, existing)
    }

    return [
      ...byThread.values(),
      ...ExtendedMedicationRequest.clusterByRxNormOrPriorPrescription(
        unthreaded
      ),
    ]
  }

  /**
   * Group medication requests into the rows a medication list should show.
   *
   * Default: union-find on RxNorm code OR priorPrescription chain, so the same
   * drug collapses even when Elation treats the orders as separate prescriptions.
   * `groupByElationThread` uses the Elation thread as the prescription identity
   * (new, refill, and dose change stay one row). RxNorm and priorPrescription
   * then apply only to requests with no thread, and those requests do not join
   * a threaded group.
   *
   * @param medications - All of a participant's medication requests
   * @param options - Grouping options
   * @param options.groupByElationThread - Group by Elation thread id, falling back to RxNorm only for requests with no thread
   */
  static groupMedications(
    medications: ExtendedMedicationRequest[],
    options: {
      groupByElationThread?: boolean
    } = {}
  ): MedicationGroup[] {
    if (medications.length === 0) {
      return []
    }

    const clusters = options.groupByElationThread
      ? ExtendedMedicationRequest.clusterByElationThread(medications)
      : ExtendedMedicationRequest.clusterByRxNormOrPriorPrescription(
          medications
        )

    /* Build MedicationGroup objects */
    return clusters.map((requests) => {
      const sortedRequests = orderBy(
        requests,
        (request) => request.effectiveDate ?? '',
        ['desc']
      )

      const mostRecent = sortedRequests[0]
      const medicationName =
        mostRecent?.medicationDisplay ??
        ExtendedMedicationRequest.extractBaseMedicationName(
          mostRecent?.medicationDisplay ?? 'Unknown Medication'
        )

      const hasPharmacyError = requests.some(
        (r) => r.pharmacyTransmissionIssue !== null
      )
      /*
       * Use .some() across the group rather than reading from mostRecent: with
       * the Elation prescription + clinic-dispense pairing pattern, mostRecent
       * can be the "DO NOT SEND" clinic-dispense with no transmission, which
       * would mask a genuine unresolved pharmacy error on the active
       * prescription sibling.
       */
      const hasUnresolvedPharmacyError = requests.some(
        (r) =>
          !!r.pharmacyTransmissionIssue &&
          !r.pharmacyTransmissionIssueResolvedAt
      )

      /* Run heuristic analysis on all requests for this medication */
      const rawRequests = requests.map((r) => r.toFhirResource())
      const supplyEstimate =
        ExtendedMedicationRequest.estimateSupplyEnd(rawRequests)
      const refillStatus =
        ExtendedMedicationRequest.analyzeRefillStatus(rawRequests)
      const medicationState =
        ExtendedMedicationRequest.classifyMedicationState(rawRequests)
      const issues = ExtendedMedicationRequest.detectIssues(rawRequests)

      /*
       * ##MedicationRepresentative Status, the displayed row, and the badge
       * all come from one request: the latest active order, unless a stopped
       * or cancelled member ends the thread over it. A later completed
       * dispense does not replace a still-active prescription, and a dispense
       * handed out after a stop does not replace that discontinuation.
       */
      const representativeRequest =
        ExtendedMedicationRequest.getRepresentative(requests) ?? mostRecent
      const currentStatus = (representativeRequest?.status ??
        'unknown') as MedicationGroup['currentStatus']

      return {
        medicationName,
        requests: sortedRequests,
        representativeRequest,
        stateRequest: representativeRequest,
        currentStatus,
        hasPharmacyError,
        hasUnresolvedPharmacyError,
        supplyEstimate,
        refillStatus,
        medicationState,
        issues,
      }
    })
  }

  /**
   * Sort medication groups by the most recent change/order.
   * Groups with the latest authoredOn date appear first.
   */
  static sortGroupsByLatestChange(
    groups: MedicationGroup[]
  ): MedicationGroup[] {
    return orderBy(groups, (group) => group.requests[0]?.effectiveDate ?? '', [
      'desc',
    ])
  }

  // Sort key placing this member on its thread's clinical timeline. See
  // medicationRecencyKey for what each segment does and why.
  get recencyKey(): string {
    return medicationRecencyKey(
      this.effectiveDate,
      this.authoredOn,
      this.elationId
    )
  }

  /**
   * Group medications by their Elation thread ID.
   * Each thread represents a medication series (New + Refills + DoseChanges).
   *
   * Returns a map where:
   * - Key = thread ID
   * - Value = all MedicationRequests in that thread, sorted by effectiveDate (newest first)
   *
   * @param medications - Array of ExtendedMedicationRequest to group
   * @returns Map of threadId -> sorted requests in that thread
   */
  static groupMedicationsByThread(
    medications: ExtendedMedicationRequest[]
  ): Map<string, ExtendedMedicationRequest[]> {
    const threads = new Map<string, ExtendedMedicationRequest[]>()

    for (const med of medications) {
      const threadId = med.threadId
      if (!threadId) {
        /* Skip medications without thread ID (e.g., non-Elation sources) */
        continue
      }

      const existing = threads.get(threadId) ?? []
      existing.push(med)
      threads.set(threadId, existing)
    }

    /* Sort each thread's requests by date (newest first) */
    for (const [threadId, requests] of threads.entries()) {
      threads.set(
        threadId,
        orderBy(requests, (request) => request.effectiveDate ?? '', ['desc'])
      )
    }

    return threads
  }

  /**
   * Build the prescription chain by following priorPrescription links.
   * Returns all prescriptions in the chain, from newest to oldest.
   *
   * Example: Current → Refill 2 → Refill 1 → Initial Rx
   *
   * @param searchFn - Function to fetch a MedicationRequest by reference
   * @returns Array of MedicationRequests in the chain (newest first)
   */
  async getPrescriptionChain(
    searchFn: (
      ref: Reference<MedicationRequest>
    ) => Promise<MedicationRequest | undefined>
  ): Promise<ExtendedMedicationRequest[]> {
    const chain: ExtendedMedicationRequest[] = [this]
    let current: ExtendedMedicationRequest = this

    /* Follow priorPrescription links backwards */
    while (current.priorPrescription) {
      const prior = await searchFn(current.priorPrescription)
      if (!prior) {
        break
      }
      const extendedPrior = ExtendedMedicationRequest.fromFhirResource(prior)
      chain.push(extendedPrior)
      current = extendedPrior

      /* Safety: prevent infinite loops */
      if (chain.length > 100) {
        console.warn('Prescription chain exceeds 100 entries, breaking')
        break
      }
    }

    return chain
  }

  /**
   * Extract base medication name (without dosage) for grouping.
   * Example: "Lisinopril 10mg tablet" -> "Lisinopril"
   */
  private static extractBaseMedicationName(fullName: string): string {
    return fullName
      .replace(/\s+\d+(\.\d+)?\s*(mg|mcg|g|ml|iu|unit).*$/i, '')
      .trim()
  }

  protected getExportFields() {
    return {
      id: this.id,
      status: this.status,
      intent: this.intent,
      medicationId: resolveId(this.medication),
      medicationCode: this.medicationCode,
      medicationDisplay: this.medicationDisplay,
      patientId: resolveId(this.subject),
      authoredOn: this.authoredOn,
      dosageInstructions: this.dosageInstructions,
      category: this.categoryCodes,
      priority: this.priority,
      note: this.noteSummary,
      startDate: this.startDate,
      stopDate: this.stopDate,
      statusReason: this.statusReason,
      type: this.type,
      refills: this.refills,
      pharmacyTransmission: this.pharmacyTransmission,
      pharmacyReception: this.pharmacyReception,
      pharmacyTransmissionIssue: this.pharmacyTransmissionIssue,
      pharmacyTransmissionIssueResolvedAt:
        this.pharmacyTransmissionIssueResolvedAt,
    }
  }
}

/* The webhook can create the draft before this handler's conditional create.
 * That resource comes back without the stamp, and later syncs only keep a
 * stamp that is already there. */
export function stampPreviousDispenserIfMissing(
  resource: MedicationRequest,
  stamp: string | undefined
): MedicationRequest | undefined {
  if (!stamp) {
    return undefined
  }
  const extended = ExtendedMedicationRequest.fromFhirResource(resource)
  if (extended.previousDispenser) {
    return undefined
  }
  extended.previousDispenser = stamp
  return extended.toFhirResource()
}
